import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
} from "react";
import { getCreditsData, getSubscriptionData, SubscriptionData, CreditsData } from "@/helpers/subscription.helper";
import { useAuth } from "./AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { showToast } from "@/helpers/toast.helpers";
import { useDialog } from "./DialogContext";
import { LINKS } from "@/constants";
import { TrialStartedToast } from "@/components/subscription/SubscriptionToasts";
import { toastStyle } from "@/config/theme";

export interface ISubscriptionType {
  subscriptionData?: SubscriptionData;
  creditsData?: CreditsData;
  subscriptionLoading?: boolean;
  subscriptionError?: any;
  utilisationError?: any;
  refetchSubscription?: () => void;
  refetchUtilisation?: () => void;
  /** Whether an ACTIVE FEATURE add-on with this name exists on the company's
   *  subscription (e.g. "AGENCY_USER_MANAGEMENT_DASHBOARD"). */
  hasAddon: (addonName: string) => boolean;
  addonsLoading?: boolean;
}

const SubscriptionContext = createContext<ISubscriptionType | undefined>(undefined);

export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscriptionContext must be used within an SubscriptionProvider");
  }
  return context;
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
    const { userDetails, currentUser } = useAuth();
    const { showDialog } = useDialog();

    // Boot LCP P3: this used to pass `roleAccess?.APP_PERMISSION`, which chained
    // the whole subscription leg behind the RBAC leg — /role/all → own-role
    // permissions → latest → utilisation-lever, ~1.6s of serial tail on dev.
    // Nothing about subscription data depends on app access, and a call made for
    // a user who turns out to have none is harmless (it is their own company's
    // subscription), so the gate is now just "we have a session": `useSubscription`
    // still requires `companyId` + `currentUser`, both available from the JWT/cookie
    // at session time. latest → utilisation stays serial (real data dependency:
    // the lever call needs `companySubscriptionId` from latest's response); folding
    // those into one backend hop is a separate follow-up.
    const { latestQuery, utilisationQuery, addonsResolved, hasAddon, subscriptionLoading, refetchUtilisation, subscriptionError, utilisationError } = useSubscription(userDetails?.company?.companyId, currentUser, true);

    const onDialogEvents = (type?: string)=>{
        if(type === 'TALK_TO_US'){
            window.open(LINKS.TALK_TO_US, '_blank');
        }
    }

    const subscriptionData = useMemo(() => {
        if(latestQuery.data && utilisationQuery.data){
            return getSubscriptionData({latest: latestQuery.data, utilization: utilisationQuery.data});
        }
        return undefined;
    }, [latestQuery.data, utilisationQuery.data]);

    const creditsData = useMemo(() => {
        if(!subscriptionLoading && latestQuery.data && utilisationQuery.data){
            return getCreditsData({latest: latestQuery.data, utilization: utilisationQuery.data});
        }
        return undefined;
    }, [latestQuery.data, subscriptionLoading, utilisationQuery.data]);

    useEffect(() => {
        if (subscriptionData) {

            if(subscriptionData.trialStartingNow){
                showToast(TrialStartedToast, {
                    duration: 6000,
                    position: 'bottom-right',
                    style: { width: '400px', ...toastStyle },
                    closeButton: true,
                    className: 'iq-trial-started-expired'
                });
                latestQuery.refetch();
            }

            if(subscriptionData.dialog){
                showDialog({...subscriptionData.dialog, onConfirm: (action)=>onDialogEvents(action?.id)});
            }
        }
    }, [subscriptionData]);

    const value = {
        subscriptionData,
        creditsData,
        subscriptionLoading: subscriptionLoading,
        refetchSubscription: latestQuery.refetch,
        refetchUtilisation,
        subscriptionError,
        utilisationError,
        hasAddon,
        // Derived from `isFetched` (not `isLoading`/`isFetching`): true only
        // once the real add-on answer is known — which requires the LATEST
        // subscription query to have settled too, not just the add-ons query
        // itself (that one sits disabled, and therefore "not loading", for
        // the entire time latestQuery is still resolving the subscription
        // id it depends on). See `addonsResolved` in useSubscription for the
        // full chain. Using `isFetched` over `isPending`/`isLoading` also
        // means a window-focus background refetch doesn't flip this
        // false→true and re-trigger Settings.tsx's page-wide loader gate —
        // same class of fix `instructionsResolving` already applies.
        addonsLoading: !addonsResolved,
    };

    return (
        <SubscriptionContext.Provider value={value}>
        {children}
        </SubscriptionContext.Provider>
    );
}
